import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import { FormControl, InputLabel, Select } from '@mui/material';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Footer from 'examples/Footer';
import { apiEditMachine, apiFileUpload } from '../../api/machine.api';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { useData } from '../../hooks';
import TopNavbar from 'examples/Navbars/TopNavbar';

const address_array = ["서울", "경기", "인천", "대전", "대구", "부산", "광주", "울산", "세종", "강원", "충북", "충남", "전북", "전남", "경북", "경남", "제주"];

const MachineEdit = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { machinedata, page, searchcond } = location.state;
  const { user, appIsReady } = useData();

  const [formData, setFormData] = useState({
    device_no: '',
    device_uuid: '',
    device_name: '',
    branch_id: '',
    m_owner_userid: '',
    setup_date: new Date().toISOString().slice(0, 10),
    address: '',
    sido: '',
    memo: '',
    information: '',
    last_signal_date: '',
    last_empty_date: '',
    status: '',
    use_time: '',
    machine_image: '',
  });

  useEffect(() => {
    if (machinedata) {
      setFormData({
        ...machinedata,
        setup_date: formatDate(machinedata.setup_date),
        last_signal_date: machinedata.last_signal_date ? formatDate(machinedata.last_signal_date) : '',
        last_empty_date: machinedata.last_empty_date ? formatDate(machinedata.last_empty_date) : ''
      });
    }
  }, [machinedata]);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [file, setFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const formatDate = (dateString) => {
    return dateString.slice(0, 10);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await apiEditMachine(formData, formData.id, user.accessToken);
      setSnackbarMessage('Machine updated successfully!');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      setTimeout(() => {
        navigate(`/machine?page=${page}&searchcond=${searchcond}`);
        setIsSubmitting(false);
      }, 500);
    } catch (error) {
      setSnackbarMessage('Failed to update machine.');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) {
      return;
    }
    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => {
      setPreviewImage(reader.result);
    };
    reader.readAsDataURL(selectedFile);
  };

  const handleUpload = async () => {
    if (!file) return;

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await apiFileUpload(uploadFormData, user.accessToken);
      const imageUrl = response;
      setFormData({
        ...formData,
        machine_image: imageUrl
      });
      setFile(null); // Clear the file input
      setPreviewImage(null); // Clear the preview image
    } catch (error) {
      console.error('Error uploading file:', error);
    }
  };

  return (
    <DashboardLayout>
      <TopNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDTypography variant="h6" color="white">
                  기기 수정
                </MDTypography>
              </MDBox>
              <MDBox p={3}>
                <form onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="제품 번호"
                        name="device_no"
                        value={formData.device_no}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="UUID"
                        name="device_uuid"
                        value={formData.device_uuid}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="기기 이름"
                        name="device_name"
                        value={formData.device_name}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="지점 ID"
                        name="branch_id"
                        value={formData.branch_id}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="소유자 ID"
                        name="m_owner_userid"
                        value={formData.m_owner_userid}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="설치 날짜"
                        name="setup_date"
                        type="date"
                        value={formData.setup_date}
                        onChange={handleInputChange}
                        required
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="주소"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <FormControl fullWidth>
                        <InputLabel id="sido-label">시/도</InputLabel>
                        <Select
                          labelId="sido-label"
                          name="sido"
                          value={formData.sido}
                          onChange={handleInputChange}
                          sx={{
                            height: 45,
                          }}
                          required
                        >
                          {address_array.map((address, index) => (
                            <MenuItem key={index} value={address}>
                              {address}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="메모"
                        name="memo"
                        value={formData.memo}
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="정보"
                        name="information"
                        value={formData.information}
                        onChange={handleInputChange}
                        multiline
                        rows={4}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="마지막 신호 날짜"
                        name="last_signal_date"
                        type="date"
                        value={formData.last_signal_date}
                        onChange={handleInputChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="마지막 비움 날짜"
                        name="last_empty_date"
                        type="date"
                        value={formData.last_empty_date}
                        onChange={handleInputChange}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel id="status-label">상태</InputLabel>
                        <Select
                          labelId="status-label"
                          name="status"
                          value={formData.status}
                          onChange={handleInputChange}
                          sx={{
                            height: 45,
                          }}
                          required
                        >
                          <MenuItem value="on">On</MenuItem>
                          <MenuItem value="off">Off</MenuItem>
                          <MenuItem value="full">Full</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="사용 시간"
                        name="use_time"
                        value={formData.use_time}
                        onChange={handleInputChange}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="기기 이미지 URL"
                        name="machine_image"
                        value={formData.machine_image}
                        onChange={handleInputChange}
                      />
                      <MDBox mt={2} display="flex" alignItems="center">
                        <input
                          accept="image/*"
                          style={{ display: 'none' }}
                          id="contained-button-file"
                          type="file"
                          onChange={handleFileChange}
                        />
                        <label htmlFor="contained-button-file">
                          <Button variant="contained" component="span" color="info">
                            파일 선택
                          </Button>
                        </label>
                        <Button variant="contained" color="success" onClick={handleUpload} sx={{ marginLeft: 2 }}>
                          업로드
                        </Button>
                      </MDBox>
                      <MDBox mt={2}>
                        {previewImage && (
                          <img src={previewImage} alt="preview" style={{ width: '20%', marginBottom: 10 }} />
                        )}
                        {!previewImage && formData.machine_image && (
                          <img src={formData.machine_image} alt="machine" style={{ width: '20%', marginBottom: 10 }} />
                        )}
                      </MDBox>
                    </Grid>
                  </Grid>
                  <MDBox mt={3} display="flex" justifyContent="flex-end">
                    <Button type="submit" variant="contained" color="success" disabled={isSubmitting}>
                      {isSubmitting ? '저장 중...' : '저장'}
                    </Button>
                  </MDBox>
                </form>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
};

export default MachineEdit;
